import { DEFAULT_FALLBACK_LOCATION } from 'constants/geoBlocking';
import type { GeographyComparisonExp } from 'types/generated/client';
import { calculateHaversineDistance } from 'utils/shared/geo/calculateHaversineDistance';
import { milesToMeters } from 'utils/shared/geo/milesToMeters';
import type { Neighborhood } from 'components/Find/Neighborhoods';

export interface LatLng {
  lat: number;
  lng: number;
}

/** How wide "near you" is when scoping /find results to the viewer's area. */
export const FIND_RADIUS_MILES = 20;

/**
 * Where a search looks with no neighborhood picked — the market we have inventory in,
 * not the viewer. Derived from the app-wide fallback so the two can't drift apart.
 */
export const DEFAULT_SEARCH_CENTER: LatLng = {
  lat: DEFAULT_FALLBACK_LOCATION.position.latitude,
  lng: DEFAULT_FALLBACK_LOCATION.position.longitude,
};

/** What the location pill reads while the search sits on DEFAULT_SEARCH_CENTER. */
export const DEFAULT_SEARCH_CENTER_NAME = 'New York';

/** A GeoJSON Point for a Hasura `geography` filter argument (`_stDWithin.from`). */
export const toGeographyPoint = (center: LatLng) => ({
  type: 'Point',
  coordinates: [center.lng, center.lat],
});

/** Great-circle distance in miles, rounded — used for the "{n}mi" labels. */
export const distanceInMiles = (from: LatLng, to: LatLng): number =>
  Math.round(
    calculateHaversineDistance({
      coord1: { latitude: from.lat, longitude: from.lng },
      coord2: { latitude: to.lat, longitude: to.lng },
      unit: 'miles',
    }),
  );

/** Rounded miles from `center` to a point, or null when either is unknown. */
export const distanceMilesTo = (
  center: LatLng | null,
  point: { latitude?: number | string | null; longitude?: number | string | null },
): number | null =>
  center && point.latitude != null && point.longitude != null
    ? distanceInMiles(center, { lat: Number(point.latitude), lng: Number(point.longitude) })
    : null;

export const neighborhoodCenter = (
  neighborhood: Pick<Neighborhood, 'latitude' | 'longitude'>,
): LatLng => ({ lat: Number(neighborhood.latitude), lng: Number(neighborhood.longitude) });

/** Points within `radiusMiles` of `center`, as a PostGIS geography filter. */
export const withinMiles = (center: LatLng, radiusMiles: number): GeographyComparisonExp => ({
  _stDWithin: { distance: milesToMeters(radiusMiles), from: toGeographyPoint(center) },
});

/**
 * The point a search is centred on, and measures its distances from. Several picked
 * neighborhoods use the first, the same rule the marketplace links follow. A viewer
 * already inside the market measures from themselves, so the ordering matches the
 * distances on the cards; the area searched stays DEFAULT_SEARCH_CENTER either way.
 */
export const searchCenterFor = (
  neighborhoods: Neighborhood[],
  viewerCenter: LatLng | null,
): LatLng => {
  if (neighborhoods[0]) return neighborhoodCenter(neighborhoods[0]);
  if (viewerCenter && distanceInMiles(viewerCenter, DEFAULT_SEARCH_CENTER) <= FIND_RADIUS_MILES) {
    return viewerCenter;
  }
  return DEFAULT_SEARCH_CENTER;
};

export const neighborhoodAreas = (neighborhoods: Neighborhood[]): GeographyComparisonExp[] =>
  neighborhoods.map((row) => withinMiles(neighborhoodCenter(row), Number(row.radiusMiles)));

/**
 * Where a search looks: inside the picked neighborhoods when there are any, wherever
 * they are, otherwise the default market.
 */
export const searchAreas = (
  radiusMiles: number,
  neighborhoods: Neighborhood[],
): GeographyComparisonExp[] =>
  neighborhoods.length > 0
    ? neighborhoodAreas(neighborhoods)
    : [withinMiles(DEFAULT_SEARCH_CENTER, radiusMiles)];
